import { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import { Dialog, Typography } from '@mui/material';
import { Header } from 'ui/Header/Header';

type Props = {
  url: string;
  fileName: string;
  open: boolean;
  headerTitle: string;
  onClose: () => void;
};

const PSPDFViewer = lazy(() => import('common/PSPDFKit/PSPDFViewer'));

const pdfViewerFallbackStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
};

const pdfViewerFallback = (
  <div
    role="status"
    aria-label="Loading PDF viewer"
    style={pdfViewerFallbackStyle}
  >
    Loading PDF viewer...
  </div>
);
const pdfViewerErrorFallback = (
  <div role="alert" style={pdfViewerFallbackStyle}>
    Unable to load PDF viewer. Please refresh and try again.
  </div>
);

export default function FileDisplayFromUrl({
  url,
  open,
  headerTitle,
  fileName,
  onClose,
}: Props) {
  const isPdf = url.split('?')[0]?.endsWith('.pdf');

  return (
    <Dialog
      open={open}
      fullScreen
      PaperProps={{ sx: { width: 'calc(100vw - 200px)' } }}
      sx={{ zIndex: 1049 }}
      maxWidth="xl"
    >
      <Header onClose={onClose} title={headerTitle} />
      {open && url && isPdf && (
        <Sentry.ErrorBoundary
          key={url}
          fallback={pdfViewerErrorFallback}
          beforeCapture={(scope) => {
            scope.setTag('component', 'PDFViewerLoader');
            scope.setTag('operation', 'loadPSPDFViewer');
          }}
        >
          <Suspense fallback={pdfViewerFallback}>
            <PSPDFViewer url={url} fileName={fileName} />
          </Suspense>
        </Sentry.ErrorBoundary>
      )}
      {url && !isPdf && (
        <iframe title="preview" src={url} width="100%" height="1000px" />
      )}
      {!url && (
        <Typography
          variant="h1"
          color="GrayText"
          display="flex"
          paddingTop="2rem"
          justifyContent="center"
        >
          Preview unavailable
        </Typography>
      )}
    </Dialog>
  );
}
